import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { RequestButton, WhatsAppButton } from "@/components/site/Buttons";
import { usePageMeta } from "@/lib/usePageMeta";
import { SERVICES } from "@/content/site";

const List = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">{title}</p>
    <ul className="mt-4 grid gap-3">
      {(items ?? []).map((i) => (
        <li key={i} className="flex gap-3 text-sm md:text-base text-[var(--grey)] leading-relaxed">
          <Check className="h-4 w-4 mt-1 shrink-0 text-[var(--red)]" aria-hidden /> {i}
        </li>
      ))}
    </ul>
  </div>
);

const ServiceRow = ({ s }) => (
  <article id={s.title.toLowerCase().replace(/\s+/g, "-")} className="group py-14 md:py-20 border-b border-[var(--line)] grid lg:grid-cols-12 gap-8 lg:gap-12 scroll-mt-28" data-testid={`service-detail-${s.code}`}>
    <Reveal className="lg:col-span-5">
      <span className="font-display font-black text-7xl md:text-8xl leading-none stroke-text">{s.code}</span>
      <h2 className="mt-4 font-display font-black uppercase text-5xl md:text-6xl leading-[0.9]">{s.title}</h2>
      <span className="mt-6 inline-flex items-center gap-2 bg-white text-[var(--ink)] px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.16em]">
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--red)]" /> {s.status === "available" ? "Available now" : "In development"}
      </span>
      <p className="mt-6 text-base text-[var(--grey)] leading-relaxed max-w-md">{s.text}</p>
      {s.example && (
        <Link to={s.example.to} className="group/l mt-6 inline-flex items-center gap-3 font-display font-extrabold uppercase tracking-[0.14em] text-sm border-b-2 border-[var(--red)] pb-1" data-testid={`service-example-${s.code}`}>
          {s.example.label} <ArrowRight className="h-4 w-4 transition-transform group-hover/l:translate-x-1" aria-hidden />
        </Link>
      )}
    </Reveal>
    <Reveal className="lg:col-span-7" delay={0.08}>
      <div className="metal metal-edge chamfer border border-[var(--line)] p-7 md:p-10 grid sm:grid-cols-2 gap-8">
        <List title="What it can involve" items={s.involves} testId={`service-involves-${s.code}`} />
        <List title="Signs you might need it" items={s.signs} testId={`service-signs-${s.code}`} />
      </div>
    </Reveal>
  </article>
);

export default function Services() {
  usePageMeta(
    "Power Tool Repair & Servicing Newcastle | Milwaukee M12 & M18 | NE Tool Repair",
    "Diagnosis, mechanical repairs, servicing and refurbishment for Milwaukee M12 & M18 power tools in Newcastle upon Tyne and the North East.",
    "/repairs-services"
  );
  return (
    <div data-testid="services-page">
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="absolute inset-0 grid-lines [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <Eyebrow>Milwaukee M12 &amp; M18</Eyebrow>
            <h1 className="mt-6 font-display font-black uppercase text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.86]">Repairs &amp;<br /><span className="text-[var(--red)]">services</span></h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={0.1}>
            <p className="text-base text-[var(--grey)] leading-relaxed">Independent power tool repair and refurbishment for tool owners in Newcastle and the North East, with a focus on Milwaukee M12 and M18 tools.</p>
          </Reveal>
        </div>
        <nav className="relative mx-auto max-w-[1400px] px-5 md:px-10 pb-10 flex flex-wrap gap-2" aria-label="Services on this page">
          {SERVICES.map((s) => (
            <a key={s.code} href={`#${s.title.toLowerCase().replace(/\s+/g, "-")}`} className="h-11 px-4 inline-flex items-center gap-3 border border-[var(--line)] text-sm font-semibold hover:border-white transition-colors" data-testid={`service-jump-${s.code}`}>
              <span className="font-mono text-xs text-[var(--red)]">{s.code}</span>{s.title}
            </a>
          ))}
        </nav>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
        {SERVICES.map((s) => <ServiceRow key={s.code} s={s} />)}
      </section>

      <section className="py-20 md:py-28 border-b border-[var(--line)] bg-[var(--char)]" data-testid="services-cta">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Not sure what you need?</Eyebrow>
            <h2 className="mt-5 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">Tell us what<br />the tool is doing</h2>
            <p className="mt-6 text-sm md:text-base text-[var(--grey)] leading-relaxed max-w-lg">Describe the fault and send a photo or two. We'll look at the details and let you know which service fits. Got another brand? Send an enquiry and we'll let you know if we can help.</p>
          </Reveal>
          <Reveal className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-3" delay={0.1}>
            <RequestButton testId="services-request-btn" />
            <WhatsAppButton testId="services-whatsapp-btn" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
